/**
 * FuturesCard — Tournament winner + top scorer picks. Selections persist from each modal on tap.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { View, Text, Animated, Easing, useWindowDimensions } from 'react-native';
import { Image } from 'expo-image';
import { Globe } from 'lucide-react-native';
import { getFlagCdnUrl } from '../../../src/worldcup/helpers/flagCdn.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { useWorldCupFuturesStats } from '../../../src/worldcup/hooks/useWorldCupFuturesStats';
import { WORLD_CUP_COMPETITION_ID } from '../../../src/worldcup/worldcup.constants';
import { getFuturesCardStyles } from './FuturesCard.styles';
import {
  FUTURES_CARD_COPY,
  FUTURES_CARD_NARROW_WIDTH,
  FUTURES_CARD_TIGHT_WIDTH,
  getFuturesCardScorerKitSize,
} from './FuturesCard.constants';
import type { PickerType } from './FuturesCard.constants';
import { SoccerKit } from '../../ui/SoccerKit';
import { FuturesPickCard } from './FuturesPickCard';
import { FuturesPickerModal } from './FuturesPickerModal';
import type { FuturesPick } from '../../../src/worldcup/hooks/useWorldCupPicksData';
import type { WorldCupFuturesOptions } from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import {
  resolveWinnerParticipant,
  scorerKitMetaForCard,
  scorerTextLabelForId,
} from './futuresCardDisplay.helper';

interface FuturesCardProps {
  futures: FuturesPick | null;
  /** After the first match kicks off, winner and top scorer cannot be changed. */
  futuresLocked: boolean;
  onSave: (
    winnerTeamId: string | null | undefined,
    topScorerPlayerId: string | null | undefined,
  ) => Promise<void>;
  futuresOptions: WorldCupFuturesOptions | null;
}

export const FuturesCard: React.FC<FuturesCardProps> = ({
  futures,
  futuresLocked,
  onSave,
  futuresOptions,
}) => {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const { width: windowWidth } = useWindowDimensions();
  const styles = useMemo(
    () => getFuturesCardStyles(theme, windowWidth, themeMode),
    [theme, windowWidth, themeMode],
  );
  const layoutNarrow = windowWidth < FUTURES_CARD_NARROW_WIDTH;
  const layoutTight = windowWidth < FUTURES_CARD_TIGHT_WIDTH;
  const scorerKitSize = getFuturesCardScorerKitSize(layoutTight, layoutNarrow);

  const futuresStats = useWorldCupFuturesStats(WORLD_CUP_COMPETITION_ID, true);

  const [draftWinner, setDraftWinner] = useState<string | null>(futures?.winnerTeamId ?? null);
  const [draftScorer, setDraftScorer] = useState<string | null>(futures?.topScorerPlayerId ?? null);
  const [activePicker, setActivePicker] = useState<PickerType | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const draftWinnerRef = useRef(draftWinner);
  const draftScorerRef = useRef(draftScorer);
  draftWinnerRef.current = draftWinner;
  draftScorerRef.current = draftScorer;

  useEffect(() => {
    setDraftWinner(futures?.winnerTeamId ?? null);
    setDraftScorer(futures?.topScorerPlayerId ?? null);
  }, [futures?.winnerTeamId, futures?.topScorerPlayerId]);

  const winnerSettled = futures?.winnerCorrect !== undefined && futures.winnerCorrect !== null;
  const scorerSettled = futures?.topScorerCorrect !== undefined && futures.topScorerCorrect !== null;
  const winnerPickDisabled = winnerSettled || futuresLocked;
  const scorerPickDisabled = scorerSettled || futuresLocked;
  const showLockedCaption = futuresLocked && (!winnerSettled || !scorerSettled);

  const winnerPickerBlocked =
    !futuresOptions || futuresOptions.participantTeams.length === 0;
  const scorerPickerBlocked =
    !futuresOptions ||
    ((futuresOptions.topScorers?.length ?? 0) === 0 && !futuresOptions.topScorerOtherId);

  const winnerPickerDisabled = winnerPickDisabled || winnerPickerBlocked;
  const scorerPickerDisabled = scorerPickDisabled || scorerPickerBlocked;
  const displayWinner = draftWinner;
  const displayScorer = draftScorer;

  const winnerTeamMeta = useMemo(
    () => resolveWinnerParticipant(displayWinner, futuresOptions?.participantTeams ?? []),
    [displayWinner, futuresOptions?.participantTeams],
  );
  const winnerFlagUri = winnerTeamMeta?.countryCode ? getFlagCdnUrl(winnerTeamMeta.countryCode) : null;
  const winnerDisplayLabel = winnerTeamMeta?.name ?? null;

  const scorerTextLabel = useMemo(
    () => scorerTextLabelForId(displayScorer, futuresOptions),
    [displayScorer, futuresOptions],
  );
  const scorerKitForCard = useMemo(
    () => scorerKitMetaForCard(displayScorer, futuresOptions),
    [displayScorer, futuresOptions],
  );
  const scorerFlagUri = useMemo(() => {
    if (!displayScorer || !scorerKitForCard) return null;
    return scorerKitForCard.countryCode !== undefined ? getFlagCdnUrl(scorerKitForCard.countryCode) : null;
  }, [displayScorer, scorerKitForCard]);

  const cardOpacity = useRef(new Animated.Value(0)).current;
  const modalTranslateY = useRef(new Animated.Value(300)).current;
  const winnerScale = useRef(new Animated.Value(1)).current;
  const scorerScale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.timing(cardOpacity, { toValue: 1, duration: 400, useNativeDriver: true }).start();
  }, [cardOpacity]);

  const closePicker = useCallback(() => setActivePicker(null), []);

  useEffect(() => {
    if (futuresLocked) closePicker();
  }, [futuresLocked, closePicker]);

  const openPicker = useCallback(
    (type: PickerType) => {
      modalTranslateY.setValue(300);
      setActivePicker(type);
      Animated.timing(modalTranslateY, {
        toValue: 0,
        duration: 280,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    },
    [modalTranslateY],
  );

  const selectWinner = useCallback(
    async (teamId: string) => {
      if (isSaving || winnerPickerDisabled) return;
      setDraftWinner(teamId);
      closePicker();
      setIsSaving(true);
      try {
        await onSave(teamId, undefined);
      } finally {
        setIsSaving(false);
      }
    },
    [isSaving, winnerPickerDisabled, closePicker, onSave],
  );

  const selectScorer = useCallback(
    async (scorerId: string) => {
      if (isSaving || scorerPickerDisabled) return;
      setDraftScorer(scorerId);
      closePicker();
      setIsSaving(true);
      try {
        await onSave(undefined, scorerId);
      } finally {
        setIsSaving(false);
      }
    },
    [isSaving, scorerPickerDisabled, closePicker, onSave],
  );

  const pressPickCard = useCallback(
    (type: PickerType) => {
      if (type === 'winner' && winnerPickerDisabled) return;
      if (type === 'scorer' && scorerPickerDisabled) return;
      const anim = type === 'winner' ? winnerScale : scorerScale;
      Animated.sequence([
        Animated.spring(anim, { toValue: 0.96, useNativeDriver: true, speed: 60, bounciness: 0 }),
        Animated.spring(anim, { toValue: 1, useNativeDriver: true, speed: 50, bounciness: 6 }),
      ]).start();
      openPicker(type);
    },
    [openPicker, winnerPickerDisabled, scorerPickerDisabled, winnerScale, scorerScale],
  );

  const pickerTitle =
    activePicker === 'winner' ? FUTURES_CARD_COPY.MODAL_WINNER : FUTURES_CARD_COPY.MODAL_SCORER;

  const renderMainFlag = (uri: string | null, label: string) => (
    <View style={styles.mainFlagPlate}>
      {uri ? (
        <Image
          source={{ uri }}
          style={styles.mainFlagPlateImage}
          contentFit="cover"
          accessibilityLabel={label}
          alt=""
        />
      ) : (
        <View style={styles.mainFlagPlateEmpty}>
          <Globe size={layoutTight ? 24 : layoutNarrow ? 26 : 28} color={theme.gray[600]} />
        </View>
      )}
    </View>
  );

  const winnerMedia = renderMainFlag(winnerFlagUri, winnerDisplayLabel ?? '');

  const scorerMedia = (
    <>
      {renderMainFlag(scorerFlagUri, scorerTextLabel ?? '')}
      <View style={styles.scorerKitOverlay}>
        <SoccerKit
          size={scorerKitSize}
          primary={scorerKitForCard?.kitColor ?? '#6B7280'}
          secondary={scorerKitForCard?.trimColor ?? '#9CA3AF'}
          number={scorerKitForCard?.number}
        />
      </View>
    </>
  );

  return (
    <Animated.View style={[styles.card, { opacity: cardOpacity }]}>
      <View style={styles.picksRow}>
        <FuturesPickCard
          pickKind="winner"
          label={FUTURES_CARD_COPY.WINNER_LABEL}
          media={winnerMedia}
          nameText={winnerDisplayLabel}
          namePlaceholder={FUTURES_CARD_COPY.PICK_TEAM}
          pickerDisabled={winnerPickerDisabled}
          isLocked={futuresLocked}
          settled={winnerSettled}
          settledCorrect={futures?.winnerCorrect}
          scale={winnerScale}
          onPress={() => pressPickCard('winner')}
          layoutTight={layoutTight}
          layoutNarrow={layoutNarrow}
          styles={styles}
          theme={theme}
        />
        <FuturesPickCard
          pickKind="scorer"
          label={FUTURES_CARD_COPY.TOP_SCORER_LABEL}
          media={scorerMedia}
          nameText={scorerTextLabel}
          namePlaceholder={FUTURES_CARD_COPY.PICK_PLAYER}
          pickerDisabled={scorerPickerDisabled}
          isLocked={futuresLocked}
          settled={scorerSettled}
          settledCorrect={futures?.topScorerCorrect}
          scale={scorerScale}
          onPress={() => pressPickCard('scorer')}
          layoutTight={layoutTight}
          layoutNarrow={layoutNarrow}
          styles={styles}
          theme={theme}
        />
      </View>

      {showLockedCaption ? (
        <View style={styles.lockedCaption}>
          <Text style={styles.lockedCaptionText}>{FUTURES_CARD_COPY.LOCKED_CAPTION}</Text>
        </View>
      ) : (
        <View style={styles.picksRowBottomSpacer} />
      )}

      <FuturesPickerModal
        activePicker={activePicker}
        isSaving={isSaving}
        draftWinner={draftWinner}
        draftScorer={draftScorer}
        modalTranslateY={modalTranslateY}
        pickerTitle={pickerTitle}
        participantTeams={futuresOptions?.participantTeams ?? []}
        topScorerOptions={futuresOptions?.topScorers ?? []}
        topScorerOtherId={futuresOptions?.topScorerOtherId}
        futuresStats={futuresStats}
        onClose={closePicker}
        onSelectWinner={selectWinner}
        onSelectScorer={selectScorer}
      />
    </Animated.View>
  );
};
